import type { SupabaseClient } from "@supabase/supabase-js";

/** Matches Supabase / GoTrue benign errors when the session is already gone. */
const BENIGN_AUTH_MSG =
  /session_not_found|session missing|Auth session missing|invalid_grant|JWT expired|JWT|refresh_token_not_found/i;

export function isBenignAuthSignOutError(err: unknown): boolean {
  if (err == null) return true;
  const o = err as { message?: string; code?: string; status?: number };
  const code = String(o.code ?? "");
  if (code === "session_not_found") return true;
  const msg = (err instanceof Error ? err.message : o.message) ?? String(err);
  if (o.status === 403 && BENIGN_AUTH_MSG.test(msg)) return true;
  return BENIGN_AUTH_MSG.test(msg);
}

/**
 * Removes persisted Supabase auth from localStorage (default key pattern: sb-*-auth-token).
 * Optional app-specific key from product requirements.
 */
export function clearSupabaseBrowserAuthStorage(): void {
  if (typeof window === "undefined") return;
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith("sb-") && key.includes("auth-token")) {
        localStorage.removeItem(key);
      }
    }
    localStorage.removeItem("rentitforward-auth");
  } catch {
    /* ignore */
  }
}

/**
 * Sign out this tab only: call GoTrue if needed, never throw on already-invalid session.
 * Always clears localStorage auth keys (covers stale client state).
 *
 * Important: call this BEFORE /api/auth/clear-session so the refresh token in localStorage
 * is still valid for POST /logout?scope=local. Clearing cookies/server session first
 * revokes the token and causes 403 session_not_found on the client signOut.
 */
export async function safeSignOutLocalClient(supabase: SupabaseClient): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    clearSupabaseBrowserAuthStorage();
    return;
  }

  try {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error && !isBenignAuthSignOutError(error)) {
      console.warn("[auth] signOut:", error.message);
    }
  } catch (e) {
    if (!isBenignAuthSignOutError(e)) {
      console.warn("[auth] signOut exception:", e);
    }
  } finally {
    clearSupabaseBrowserAuthStorage();
  }
}
